import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './BottomSheet.module.css';

type Props = {
  /** Heading under the handle; leave out when the content has its own. */
  title?: ReactNode;
  close: () => void;
  children: ReactNode;
  /** Nearly the whole screen (a page in a sheet); otherwise as tall as its
   *  content. */
  tall?: boolean;
  className?: string;
};

/** How far down the handle must be pulled to close the sheet (px). */
const CLOSE_DRAG = 90;

/**
 * Modern layout: a sheet sliding up from the bottom, the page dimmed above
 * it. A tap on the page above, Esc, or pulling the handle down closes it;
 * the page behind does not scroll. `close` should keep its identity between
 * renders (useCallback).
 */
export const BottomSheet = ({
  title,
  close,
  children,
  tall = false,
  className,
}: Props) => {
  const { t } = useTranslation();
  const [drag, setDrag] = useState(0);
  const startY = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [close]);

  const onDown = (e: ReactPointerEvent) => {
    startY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent) => {
    if (startY.current == null) return;
    setDrag(Math.max(0, e.clientY - startY.current));
  };
  const onUp = () => {
    if (startY.current == null) return;
    const pulled = drag;
    startY.current = null;
    setDrag(0);
    if (pulled > CLOSE_DRAG) close();
  };

  return createPortal(
    <div className={styles.root}>
      <div className={styles.scrim} onClick={close} />
      <div
        className={classNames(styles.sheet, tall && styles.tall, className)}
        role="dialog"
        aria-modal="true"
        style={
          drag
            ? { transform: `translateY(${drag}px)`, transition: 'none' }
            : undefined
        }
      >
        <div
          className={styles.grip}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <button
            type="button"
            className={styles.handle}
            aria-label={t('close_label')}
            onClick={() => drag === 0 && close()}
          />
          {title && <h3 className={styles.title}>{title}</h3>}
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body
  );
};
